import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ChangePassword from "./pages/ChangePassword";
import DriverJobs from "./pages/DriverJobs";
import ContainerQuotation from "./pages/ContainerQuotation";
import TermsAndConditions from "./pages/TermsAndConditions";
import JobCalendar from "./pages/JobCalendar";

// Attach admin/staff identity to all API calls (used for audit stamping + auth)
axios.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    const token = localStorage.getItem("njk_admin_token");
    if (token && token !== "google-session" && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const user = localStorage.getItem("njk_admin_user");
    if (user && !config.headers["X-Actor-Name"]) {
      config.headers["X-Actor-Name"] = encodeURIComponent(user);
      config.headers["X-Actor-Role"] = localStorage.getItem("njk_admin_role") || "owner";
    }
  }
  return config;
});
import AuthCallback from "./pages/AuthCallback";
import StaffManagement from "./pages/StaffManagement";
import StaffLogin from "./pages/StaffLogin";
import GSTSettings from "./pages/GSTSettings";
import AgingReport from "./pages/AgingReport";
import CustomerPortal from "./pages/CustomerPortal";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { WhatsAppButton } from "./components/WhatsAppButton";
import Home from "./pages/Home";
import { Services } from "./pages/Services";
import { ServiceDetail } from "./pages/ServiceDetail";
import { About } from "./pages/About";
import { Booking } from "./pages/Booking";
import { Contact } from "./pages/Contact";
import { Gallery } from "./pages/Gallery";
import { BookingManagement } from "./pages/BookingManagement";
import { BillingDashboard } from "./pages/BillingDashboard";
import { CreateQuotation } from "./pages/CreateQuotation";
import { CreateInvoice } from "./pages/CreateInvoice";
import { CreateDeliveryOrder } from "./pages/CreateDeliveryOrder";
import { CreatePaymentReceipt } from "./pages/CreatePaymentReceipt";
import { QuotationDetail } from "./pages/QuotationDetail";
import { InvoiceDetail } from "./pages/InvoiceDetail";
import { DeliveryOrderDetail } from "./pages/DeliveryOrderDetail";
import { PaymentReceiptDetail } from "./pages/PaymentReceiptDetail";
import { EditQuotation } from "./pages/EditQuotation";
import { EditInvoice } from "./pages/EditInvoice";
import { EditDeliveryOrder } from "./pages/EditDeliveryOrder";
import { AdminTools } from "./pages/AdminTools";
import { CreateSOA } from "./pages/CreateSOA";
import { SOADetail } from "./pages/SOADetail";
import { EditSOA } from "./pages/EditSOA";
import { Accounting } from "./pages/Accounting";
import { Customers } from "./pages/Customers";
import { PayslipDashboard } from "./pages/PayslipDashboard";
import { CreatePayslip } from "./pages/CreatePayslip";
import { PayslipDetail } from "./pages/PayslipDetail";
import { WarehouseDashboard } from "./pages/WarehouseDashboard";
import { CreateWarehouseQuotation } from "./pages/CreateWarehouseQuotation";
import { WarehouseQuotationDetail } from "./pages/WarehouseQuotationDetail";
import { WarehouseInvoiceDetail } from "./pages/WarehouseInvoiceDetail";
import { EditWarehouseQuotation } from "./pages/EditWarehouseQuotation";
import { EditWarehouseInvoice } from "./pages/EditWarehouseInvoice";
import InventoryDashboard from "./pages/InventoryDashboard";
import CreateInventory from "./pages/CreateInventory";
import { InventoryDetail } from "./pages/InventoryDetail";
import EditInventory from "./pages/EditInventory";
import { TestimonialsAdmin } from "./pages/TestimonialsAdmin";
import MyNotes from "./pages/MyNotes";
import { ProfitTracker } from "./pages/ProfitTracker";
import { AdminLogin } from "./pages/AdminLogin";
import { ProtectedRoute } from "./components/ProtectedRoute";
import PublicSignDeliveryOrder from "./pages/PublicSignDeliveryOrder";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import PayInvoice from "./pages/PayInvoice";
import PublicSignQuotation from "./pages/PublicSignQuotation";
import Contracts from "./pages/Contracts";
import PublicSignContract from "./pages/PublicSignContract";
import EquipmentManagement from "./pages/EquipmentManagement";
import LorryManagement from "./pages/LorryManagement";
import EmployeeManagement from "./pages/EmployeeManagement";
import OperatorLogin from "./pages/OperatorLogin";
import OperatorChecklist from "./pages/OperatorChecklist";
import MaintenanceChecklists from "./pages/MaintenanceChecklists";
import QRCodes from "./pages/QRCodes";
import SmartCard from "./pages/SmartCard";
import ENameCard from "./pages/ENameCard";
import SupplierManagement from "./pages/SupplierManagement";
import DispatchHistory from "./pages/DispatchHistory";
import GSTReport from "./pages/GSTReport";
import PaymentVouchers from "./pages/PaymentVouchers";
import Payments from "./pages/Payments";
import Schedule from "./pages/Schedule";
import { Toaster } from "./components/ui/sonner";

// Detect Emergent OAuth callback synchronously during render (must use useLocation().hash)
const GoogleAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GoogleAuthGate>
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route path="/card" element={<SmartCard />} />
          <Route path="/connect" element={<SmartCard />} />
          <Route path="/card/:person" element={<ENameCard />} />
          <Route path="/sign/do/:token" element={<PublicSignDeliveryOrder />} />
          <Route path="/pay/success" element={<PaymentSuccess />} />
          <Route path="/pay/cancelled" element={<PaymentCancelled />} />
          <Route path="/pay/invoice/:id" element={<PayInvoice />} />
          <Route path="/sign/quotation/:token" element={<PublicSignQuotation />} />
          <Route path="/sign/contract/:token" element={<PublicSignContract />} />
          <Route path="/admin/contracts" element={<ProtectedRoute ownerOnly><Contracts /></ProtectedRoute>} />
          <Route path="/admin/bookings" element={<ProtectedRoute><BookingManagement /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
          <Route path="/admin/billing/create-quotation" element={<ProtectedRoute fullBilling><CreateQuotation /></ProtectedRoute>} />
          <Route path="/admin/billing/create-invoice" element={<ProtectedRoute><CreateInvoice /></ProtectedRoute>} />
          <Route path="/admin/billing/create-delivery-order" element={<ProtectedRoute><CreateDeliveryOrder /></ProtectedRoute>} />
          <Route path="/admin/billing/create-payment-receipt" element={<ProtectedRoute fullBilling><CreatePaymentReceipt /></ProtectedRoute>} />
          <Route path="/admin/billing/quotation/:id" element={<ProtectedRoute fullBilling><QuotationDetail /></ProtectedRoute>} />
          <Route path="/admin/billing/container-quotation" element={<ProtectedRoute fullBilling><ContainerQuotation /></ProtectedRoute>} />
          <Route path="/admin/billing/calendar" element={<ProtectedRoute><JobCalendar /></ProtectedRoute>} />
          <Route path="/admin/billing/quotation/:id/edit" element={<ProtectedRoute fullBilling><EditQuotation /></ProtectedRoute>} />
          <Route path="/admin/quotations/:id" element={<ProtectedRoute fullBilling><QuotationDetail /></ProtectedRoute>} />
          <Route path="/admin/quotations/:id/edit" element={<ProtectedRoute fullBilling><EditQuotation /></ProtectedRoute>} />
          <Route path="/admin/billing/invoice/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
          <Route path="/admin/billing/invoice/:id/edit" element={<ProtectedRoute><EditInvoice /></ProtectedRoute>} />
          <Route path="/admin/billing/delivery-order/:id" element={<ProtectedRoute><DeliveryOrderDetail /></ProtectedRoute>} />
          <Route path="/admin/billing/delivery-order/:id/edit" element={<ProtectedRoute><EditDeliveryOrder /></ProtectedRoute>} />
          <Route path="/admin/billing/receipt/:id" element={<ProtectedRoute fullBilling><PaymentReceiptDetail /></ProtectedRoute>} />
          <Route path="/admin/tools" element={<ProtectedRoute ownerOnly><AdminTools /></ProtectedRoute>} />
          <Route path="/admin/staff" element={<ProtectedRoute ownerOnly><StaffManagement /></ProtectedRoute>} />
          <Route path="/admin/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/admin/gst-settings" element={<ProtectedRoute ownerOnly><GSTSettings /></ProtectedRoute>} />
          <Route path="/admin/aging-report" element={<ProtectedRoute ownerOnly><AgingReport /></ProtectedRoute>} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/driver/:name" element={<DriverJobs />} />
          <Route path="/admin/billing/create-soa" element={<ProtectedRoute fullBilling><CreateSOA /></ProtectedRoute>} />
          <Route path="/admin/billing/soa/:id" element={<ProtectedRoute fullBilling><SOADetail /></ProtectedRoute>} />
          <Route path="/admin/billing/soa/:id/edit" element={<ProtectedRoute fullBilling><EditSOA /></ProtectedRoute>} />
          <Route path="/admin/accounting" element={<ProtectedRoute ownerOnly><Accounting /></ProtectedRoute>} />
          <Route path="/admin/equipment" element={<ProtectedRoute ownerOnly><EquipmentManagement /></ProtectedRoute>} />
          <Route path="/admin/lorries" element={<ProtectedRoute ownerOnly><LorryManagement /></ProtectedRoute>} />
          <Route path="/admin/employees" element={<ProtectedRoute ownerOnly><EmployeeManagement /></ProtectedRoute>} />
          <Route path="/admin/maintenance-checklists" element={<ProtectedRoute ownerOnly><MaintenanceChecklists /></ProtectedRoute>} />
          <Route path="/admin/qr-codes" element={<ProtectedRoute ownerOnly><QRCodes /></ProtectedRoute>} />
          <Route path="/operator/login" element={<OperatorLogin />} />
          <Route path="/operator/checklist" element={<OperatorChecklist />} />
          <Route path="/admin/customers" element={<ProtectedRoute ownerOnly><Customers /></ProtectedRoute>} />
          <Route path="/admin/payslip" element={<ProtectedRoute ownerOnly><PayslipDashboard /></ProtectedRoute>} />
          <Route path="/admin/payslip/create" element={<ProtectedRoute ownerOnly><CreatePayslip /></ProtectedRoute>} />
          <Route path="/admin/payslip/:id" element={<ProtectedRoute ownerOnly><PayslipDetail /></ProtectedRoute>} />
          <Route path="/admin/warehouse" element={<ProtectedRoute ownerOnly><WarehouseDashboard /></ProtectedRoute>} />
          <Route path="/admin/warehouse/create-quotation" element={<ProtectedRoute fullBilling><CreateWarehouseQuotation /></ProtectedRoute>} />
          <Route path="/admin/warehouse/quotation/:id" element={<ProtectedRoute fullBilling><WarehouseQuotationDetail /></ProtectedRoute>} />
          <Route path="/admin/warehouse/quotation/:id/edit" element={<ProtectedRoute fullBilling><EditWarehouseQuotation /></ProtectedRoute>} />
          <Route path="/admin/inventory" element={<ProtectedRoute ownerOnly><InventoryDashboard /></ProtectedRoute>} />
          <Route path="/admin/inventory/create" element={<ProtectedRoute ownerOnly><CreateInventory /></ProtectedRoute>} />
          <Route path="/admin/inventory/:id" element={<ProtectedRoute ownerOnly><InventoryDetail /></ProtectedRoute>} />
          <Route path="/admin/inventory/:id/edit" element={<ProtectedRoute ownerOnly><EditInventory /></ProtectedRoute>} />
          <Route path="/admin/warehouse/invoice/:id" element={<ProtectedRoute fullBilling><WarehouseInvoiceDetail /></ProtectedRoute>} />
          <Route path="/admin/warehouse/invoice/:id/edit" element={<ProtectedRoute fullBilling><EditWarehouseInvoice /></ProtectedRoute>} />
          <Route path="/admin/testimonials" element={<ProtectedRoute ownerOnly><TestimonialsAdmin /></ProtectedRoute>} />
          <Route path="/admin/notes" element={<ProtectedRoute ownerOnly><MyNotes /></ProtectedRoute>} />
          <Route path="/admin/profit-tracker" element={<ProtectedRoute ownerOnly><ProfitTracker /></ProtectedRoute>} />
          <Route path="/admin/suppliers" element={<ProtectedRoute ownerOnly><SupplierManagement /></ProtectedRoute>} />
          <Route path="/admin/dispatch-history" element={<ProtectedRoute ownerOnly><DispatchHistory /></ProtectedRoute>} />
          <Route path="/admin/gst-report" element={<ProtectedRoute ownerOnly><GSTReport /></ProtectedRoute>} />
          <Route path="/admin/payment-vouchers" element={<ProtectedRoute ownerOnly><Payments /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute ownerOnly><Payments /></ProtectedRoute>} />
          <Route path="/admin/schedule" element={<ProtectedRoute ownerOnly><Schedule /></ProtectedRoute>} />
        </Routes>
        <Footer />
        <WhatsAppButton />
        </GoogleAuthGate>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
